import { useQuery } from '@apollo/client/react/index.js';
import {
  AnimatedCounter,
  cn,
  GlassBadge,
  GlassButton,
  GlassCard,
  GlassCardContent,
  GlassEmptyState,
  GlassInput,
  SkeletonLoader,
} from '@merritrac/shared/components';
import { Copy, Edit2, FileText, MapPin, Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { GET_CONTRACTS } from '../lib/graphql/operations.js';

import type { ReactElement } from 'react';

/* ── GraphQL response types ── */

interface GqlChecklist {
  accessKey: number;
  linkedAk: number;
  location: string;
  seq: number;
  item: string;
  mobileFieldType: string;
  displayList: string | null;
  group: string | null;
  showWhen: string | null;
}

interface GqlContractNode {
  accessKey: number;
  contractNumber: string | null;
  contractType: string | null;
  serviceLevel: string | null;
  serviceOrderAk: number;
  checklists: GqlChecklist[];
}

interface GqlContractEdge {
  node: GqlContractNode;
}

interface GqlContractsConnection {
  totalCount: number;
  pageInfo: {
    hasNextPage: boolean;
    endCursor: string | null;
  };
  edges: GqlContractEdge[];
}

interface GetContractsData {
  contracts: GqlContractsConnection;
}

/* ── UI types ── */

type ChecklistStatus = 'Active' | 'Draft' | 'Archived';

type FieldTypeName = 'Text' | 'Boolean' | 'Picker' | 'Image' | 'Date' | 'Notes';

interface FieldTypeBreakdown {
  type: FieldTypeName;
  count: number;
}

interface ChecklistTemplate {
  id: string;
  name: string;
  contractName: string;
  totalFields: number;
  locationCount: number;
  fieldTypes: FieldTypeBreakdown[];
  status: ChecklistStatus;
  lastModified: string;
}

/* ── Helpers ── */

const STATUS_VARIANT: Record<ChecklistStatus, 'success' | 'warning' | 'default'> = {
  Active: 'success',
  Draft: 'warning',
  Archived: 'default',
};

const FIELD_TYPE_COLOR: Record<FieldTypeName, string> = {
  Text: 'bg-info-soft text-info-fg',
  Boolean: 'bg-success-soft text-success-fg',
  Picker: 'bg-warning-soft text-warning-fg',
  Image: 'bg-primary-soft text-primary',
  Date: 'bg-bg-muted text-text-secondary',
  Notes: 'bg-danger-soft text-danger-fg',
};

const DISPLAY_TYPE_MAP: Record<string, FieldTypeName> = {
  text: 'Text',
  input: 'Text',
  boolean: 'Boolean',
  checkbox: 'Boolean',
  picker: 'Picker',
  dropdown: 'Picker',
  image: 'Image',
  photo: 'Image',
  date: 'Date',
  notes: 'Notes',
  textarea: 'Notes',
};

function normalizeDisplayType(displayType: string | null | undefined): FieldTypeName {
  if (displayType === null || displayType === undefined || displayType === '') {
    return 'Text';
  }
  return DISPLAY_TYPE_MAP[displayType.toLowerCase()] ?? 'Text';
}

function deriveFieldTypes(checklists: GqlChecklist[]): FieldTypeBreakdown[] {
  const counts: Partial<Record<FieldTypeName, number>> = {};
  for (const cl of checklists) {
    const ft = normalizeDisplayType(cl.mobileFieldType);
    counts[ft] = (counts[ft] ?? 0) + 1;
  }
  return (Object.entries(counts) as [FieldTypeName, number][]).map(([type, count]) => ({
    type,
    count,
  }));
}

function mapContractToTemplate(node: GqlContractNode): ChecklistTemplate {
  const checklists: GqlChecklist[] = node.checklists;
  const locations = Array.from(new Set(checklists.map((cl) => cl.location)));
  return {
    id: String(node.accessKey),
    name: node.contractNumber ?? '—',
    contractName: node.contractNumber ?? '—',
    totalFields: checklists.length,
    locationCount: locations.length,
    fieldTypes: deriveFieldTypes(checklists),
    status: 'Active',
    lastModified: '',
  };
}

function formatDate(iso: string): string {
  if (iso === '') {
    return '—';
  }
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/* ── ChecklistCard ── */

interface ChecklistCardProps {
  template: ChecklistTemplate;
  onEdit?: () => void;
}

function ChecklistCard({ template, onEdit }: ChecklistCardProps): ReactElement {
  return (
    <GlassCard className="flex flex-col hover:shadow-md transition-shadow duration-[var(--dur)]">
      <GlassCardContent className="flex flex-col gap-4 h-full">
        {/* Top row: name + status */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2.5 min-w-0">
            <div className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-lg bg-primary-soft mt-0.5">
              <FileText size={16} className="text-primary" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-text leading-snug">{template.name}</h3>
              <p className="text-xs text-text-tertiary mt-0.5 truncate">{template.contractName}</p>
            </div>
          </div>
          <GlassBadge variant={STATUS_VARIANT[template.status]} className="flex-shrink-0">
            {template.status}
          </GlassBadge>
        </div>

        {/* Stats row */}
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1.5 text-text-secondary">
            <span className="font-semibold text-text">
              <AnimatedCounter value={template.totalFields} />
            </span>
            <span>fields</span>
          </div>
          <div className="w-px h-4 bg-border-subtle" />
          <div className="flex items-center gap-1.5 text-text-secondary">
            <MapPin size={13} className="text-text-tertiary" />
            <span className="font-semibold text-text">
              <AnimatedCounter value={template.locationCount} />
            </span>
            <span>{template.locationCount === 1 ? 'location' : 'locations'}</span>
          </div>
        </div>

        {/* Field type breakdown */}
        <div className="flex flex-wrap gap-1.5">
          {template.fieldTypes.map((ft) => (
            <span
              key={ft.type}
              className={cn(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                FIELD_TYPE_COLOR[ft.type],
              )}
            >
              <span className="opacity-75">{ft.type}:</span>
              <span>{ft.count}</span>
            </span>
          ))}
        </div>

        {/* Footer: date + actions */}
        <div className="flex items-center justify-between pt-1 mt-auto border-t border-border-subtle">
          <span className="text-xs text-text-tertiary">
            {template.lastModified !== '' ? `Modified ${formatDate(template.lastModified)}` : '—'}
          </span>
          <div className="flex items-center gap-1">
            <GlassButton
              variant="ghost"
              size="sm"
              className="gap-1"
              onClick={() => {
                onEdit?.();
              }}
            >
              <Edit2 size={12} />
              Edit
            </GlassButton>
            <GlassButton variant="ghost" size="sm" className="gap-1">
              <Copy size={12} />
              Duplicate
            </GlassButton>
          </div>
        </div>
      </GlassCardContent>
    </GlassCard>
  );
}

/* ── Page ── */

export function ChecklistsPage(): ReactElement {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const { data, loading, error } = useQuery<GetContractsData>(GET_CONTRACTS, {
    variables: { first: 50 },
  });

  const templates: ChecklistTemplate[] =
    data?.contracts.edges.map((e) => mapContractToTemplate(e.node)) ?? [];

  const filtered = templates.filter((t) => {
    const q = search.toLowerCase();
    return (
      t.name.toLowerCase().includes(q) ||
      t.contractName.toLowerCase().includes(q) ||
      t.status.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-text-secondary">
          Inspection templates linked to service contracts
        </p>
        <GlassButton
          variant="primary"
          size="md"
          className="self-start sm:self-auto"
          onClick={() => {
            void navigate('/checklists/new');
          }}
        >
          <Plus size={15} />
          New Template
        </GlassButton>
      </div>

      {/* Search + count bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="relative w-full sm:w-72">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none"
          />
          <GlassInput
            placeholder="Search templates..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            className="pl-8 w-full"
          />
        </div>
        <span className="text-sm text-text-tertiary flex-shrink-0">
          {loading
            ? '…'
            : `${filtered.length.toString()} ${filtered.length === 1 ? 'template' : 'templates'}`}
        </span>
      </div>

      {/* Loading / error / card grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <SkeletonLoader className="h-48 w-full rounded-xl" />
          <SkeletonLoader className="h-48 w-full rounded-xl" />
          <SkeletonLoader className="h-48 w-full rounded-xl" />
        </div>
      ) : error !== undefined ? (
        <div className="py-12 text-center text-sm text-danger">
          Failed to load checklists: {error.message}
        </div>
      ) : filtered.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((template) => (
            <ChecklistCard
              key={template.id}
              template={template}
              onEdit={() => {
                void navigate(`/checklists/${template.id}/edit`);
              }}
            />
          ))}
        </div>
      ) : (
        <GlassEmptyState
          icon={FileText}
          title="No templates found"
          description="Try a different search term or create a new template."
        />
      )}
    </div>
  );
}
